import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { createInvitation, getRecommendations, listMembers, listInvitations } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../ui/toast';

const DISMISS_KEY = 'lizrd.inviteNudge.dismissed';

/**
 * Post-activation "invite your team" nudge (audit F7). Inviting teammates used to
 * sit mid-wizard, before the user saw any value; now it surfaces here on the
 * dashboard — dismissible, admin-only, and only once there's something worth
 * sharing (real recommendations) and the org is still a solo seat (no other
 * members, no pending invites).
 */
export function InviteTeamNudge({ domainId }: { domainId: string }) {
  const role = useAuthStore((s) => s.user?.role);
  const canInvite = role ? roleHasPermissions(role, [Permission.UsersManage]) : false;
  const { toast } = useToast();
  const qc = useQueryClient();

  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === 'true');
  const [email, setEmail] = useState('');

  const members = useQuery({ queryKey: ['members'], queryFn: listMembers, enabled: canInvite });
  const invites = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
    enabled: canInvite,
  });
  // Only nudge when there's value to share: this domain has recommendations.
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    enabled: canInvite,
  });

  const invite = useMutation({
    mutationFn: () => createInvitation({ email: email.trim(), role: 'ENGINEER', domainIds: [] }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['invitations'] });
      toast({ title: 'Invite sent', description: `We emailed ${email.trim()}.`, tone: 'success' });
      setEmail('');
    },
    onError: (e) =>
      toast({
        title: 'Could not send invite',
        description: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      }),
  });

  if (!canInvite || dismissed) return null;
  const solo = (members.data?.length ?? 1) <= 1 && (invites.data?.length ?? 0) === 0;
  const hasValue = (recs.data?.items.length ?? 0) > 0;
  if (!solo || !hasValue) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (email.trim()) invite.mutate();
  }

  return (
    <Card className="border-iris/30 bg-iris/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-sm font-bold text-bone">
            Share these wins with your team
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Savings get realized by the people who apply the fixes. Invite an engineer to this
            workspace — they join with role-based access and only see what you grant.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex-none rounded-sm px-2 py-0.5 text-xs text-muted transition-colors hover:text-bone"
          aria-label="Dismiss"
        >
          Dismiss ✕
        </button>
      </div>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <Input
          type="email"
          placeholder="teammate@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="max-w-xs"
        />
        <Button type="submit" size="sm" disabled={!email.trim() || invite.isPending}>
          {invite.isPending ? 'Inviting…' : 'Send invite'}
        </Button>
      </form>
    </Card>
  );
}
